import "@assets/css/bootstrap-grid.css";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import {
  IndustryAlliesFormData,
  IndustryAlliesSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { IndustryAlliesData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router";

const IndustryForm: React.FC = () => {
  const queryClient = useQueryClient();
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const modalItem = searchParams.get("modal-item");
  const pathname = usePathname();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IndustryAlliesFormData>({
    mode: "onChange",
    resolver: zodResolver(IndustryAlliesSchema),
    defaultValues: {
      name: modalItem === "add" ? (searchParams.get("search") as string) : "",
    },
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();

  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<IndustryAlliesData>
  >({
    endpoint: endpoint["industryallies"],
    id: modalItem || "",
    key: "industryallies" + modalItem,
    enabled: modalItem ? true : false,
  });

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (searchParams.get("modal") === "add") {
      return reset({});
    }

    if (data && searchParams.get("modal") !== "add") {
      reset({
        image: data?.data?.image,
        name: data?.data?.name,
      });
    }
  }, [data, fetchPending]);
  const { mutate, isPending } = usePostData();

  /* mutate action */
  const handleMutate = (data: any) => {
    setIsFormPending(true);

    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };
      // if (!(data.image instanceof File || data.image instanceof Blob))
      //   delete data.image;
    }

    return {
      data,
      endpoint: endpoint["industryallies"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      setIsFormPending(false);
      handleAction({ type: "modal-close" });
      showToast({
        description: `Industry Allies ${
          status === 200 ? "Updated" : "Created"
        }`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };

  const onSubmit: SubmitHandler<IndustryAlliesFormData> = (data) => {
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema = React.useMemo(
    () => [
      {
        name: "name",
        label: "Name",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Name",
        control,
        setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "image",
        label: "Upload Image",
        colSpan: 12,
        type: "cropper",
        control,
        setValue,
        cropperWidthPlaceholder: 2560,
        cropperHeightPlaceholder: 730,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        type: "submit",
        value: `${searchParams.get("modal") || ""} ${(
          type || pathname
        )?.replace("-", " ")}`,
        className: "w-full h-[47px] mt-[32px] capitalize",
        colSpan: 12,
        variant: "default",
        endIcon: isPending ? <Spinner /> : null,
        disabled: isPending ? true : false,
        visable: true,
      },
    ],
    [control, setValue, errors, isPending]
  );

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default IndustryForm;
